/**
 * GraphNodeHeader - Header section of a graph node
 */

import { useCallback, useState, useEffect, useMemo } from 'react';
import { useReactFlow } from '@xyflow/react';
import { useScope } from '../../contexts/ScopeContext';
import { nodeHeaderButton, nodeHeaderButtonActive, nodeHeaderButtonError } from './nodeHeaderStyles'
import { logger, LogLevel } from '@/logging';
import { useAppState } from '@/ui/contexts/AppContext';

import { useRuntime } from '@/ui/contexts/RuntimeProvider';
import { EditableLabel } from './EditableLabel';
import { useGraphNode } from './useGraphNode';

// Layout constants - must match GraphNode
const HEADER_PADDING = 8;
const HEADER_LINE1 = 16;
const HEADER_LINE2 = 13;
const HEADER_BORDER = 1;

export const HEADER_HEIGHT = HEADER_PADDING + HEADER_LINE1 + HEADER_LINE2 + HEADER_PADDING + HEADER_BORDER;

interface GraphNodeHeaderProps {
  nodeId: string;
  nodeType: string;
  label?: string;
  info?: string;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
}

export function GraphNodeHeader({ nodeId, nodeType, label, info, isCollapsed, onToggleCollapse }: GraphNodeHeaderProps) {
  const { instanceId, setInstance, selectedNodeIds } = useScope();
  const { setNodes } = useReactFlow();
  const [logEntries, setLogEntries] = useState(logger.getAll());
  const { inspecting, setInspecting } = useAppState()
  const { hasDefinition } = useRuntime()

  const [showInfo, setShowInfo] = useState(false);

  const isInspected = inspecting && inspecting.instanceId === instanceId && inspecting.nodeId === nodeId;
  const isSelected = selectedNodeIds.includes(nodeId)

  const { tryDelete } = useGraphNode(instanceId, nodeId)

  // Check if this node is a sub-graph (has a definition)
  const isSubGraph = hasDefinition(nodeType);

  const toggleInspecting = useCallback(() => {
    if (inspecting?.nodeId === nodeId && inspecting?.instanceId === instanceId) {
      setInspecting(null)
    } else {
      setInspecting({ instanceId, nodeId })
    }
  },[inspecting,setInspecting, nodeId,instanceId])

  // Subscribe to log changes to detect errors
  useEffect(() => {
    const unsubscribe = logger.subscribe(() => {
      setLogEntries(logger.getAll());
    });
    return unsubscribe;
  }, []);

  // Check if this node has errors
  const hasErrors = useMemo(() =>
    logEntries.some(e => e.context === nodeId && e.level === LogLevel.Error),
    [logEntries, nodeId]
  );

  const handleEnterSubgraph = useCallback(() => {
    if (isSubGraph) {
      setInstance(nodeId);
    }
  }, [isSubGraph, nodeId, setInstance]);

  // Click on header with modifier key to toggle selection
  const handleHeaderClick = useCallback((e: React.MouseEvent) => {
    if (e.ctrlKey || e.metaKey) {
      e.stopPropagation();
      setNodes(nodes => nodes.map(n =>
        n.id === nodeId ? { ...n, selected: !n.selected } : n
      ));
    }
  }, [nodeId, setNodes]);


  return (
    <div
      onClick={handleHeaderClick}
      style={{
        background: isSelected ? '#1e40af' : '#374151',
        padding: `${HEADER_PADDING}px 12px`,
        borderRadius: '6px 6px 0 0',
        borderBottom: `${HEADER_BORDER}px solid ${isSelected ? '#3b82f6' : '#4b5563'}`,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        cursor: 'default'
      }}
    >
      <div
        style={{ cursor: isSubGraph ? 'pointer' : 'default', minWidth: 0, overflow: 'hidden' }}
      >
        <EditableLabel
          nodeId={nodeId}
          label={label}
          onDoubleClick={isSubGraph ? handleEnterSubgraph : undefined}
          style={{
            fontWeight: 'bold',
            fontSize: 13,
            lineHeight: `${HEADER_LINE1}px`,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
          inputStyle={{
            fontWeight: 'bold',
            fontSize: 13,
            lineHeight: `${HEADER_LINE1}px`,
            color: 'inherit',
          }}
        />
        <div style={{ fontSize: 10, color: '#9ca3af', lineHeight: `${HEADER_LINE2}px` }}>{nodeType}</div>
      </div>
      <div style={{ display: 'flex', gap: 2, position: 'relative', alignItems: 'center' }}>
        {info && (
          <>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowInfo(!showInfo);
              }}
              style={showInfo ? nodeHeaderButtonActive : nodeHeaderButton}
              onMouseEnter={(e) => {
                if (!showInfo) {
                  Object.assign((e.target as HTMLButtonElement).style, nodeHeaderButtonActive);
                }
              }}
              onMouseLeave={(e) => {
                if (!showInfo) {
                  Object.assign((e.target as HTMLButtonElement).style, nodeHeaderButton);
                }
              }}
              title="Show help info"
            >
              ?
            </button>
            {showInfo && (
              <div
                style={{
                  position: 'absolute',
                  bottom: '100%',
                  right: 0,
                  marginBottom: 4,
                  background: '#1f2937',
                  border: '1px solid #4b5563',
                  borderRadius: 6,
                  padding: '8px 12px',
                  fontSize: 11,
                  lineHeight: 1.4,
                  color: '#d1d5db',
                  maxWidth: 280,
                  minWidth: 180,
                  zIndex: 100,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                  whiteSpace: 'pre-wrap'
                }}
                onClick={(e) => e.stopPropagation()}
              >
                {info}
              </div>
            )}
          </>
        )}
        {onToggleCollapse && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggleCollapse();
            }}
            style={isCollapsed ? nodeHeaderButtonActive : nodeHeaderButton}
            onMouseEnter={(e) => {
              if (!isCollapsed) {
                Object.assign((e.target as HTMLButtonElement).style, nodeHeaderButtonActive);
              }
            }}
            onMouseLeave={(e) => {
              if (!isCollapsed) {
                Object.assign((e.target as HTMLButtonElement).style, nodeHeaderButton);
              }
            }}
            title={isCollapsed ? "Expand endpoints" : "Collapse endpoints"}
          >
            <span style={{
              display: 'inline-block',
              transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
              transition: 'transform 0.15s ease',
              fontSize: 8
            }}>▼</span>
          </button>
        )}
        {(
          <button
            onClick={(e) => {
              e.stopPropagation();
              toggleInspecting();
            }}
            style={hasErrors ? nodeHeaderButtonError : (isInspected ? nodeHeaderButtonActive : nodeHeaderButton)}
            onMouseEnter={(e) => {
              if (!isInspected && !hasErrors) {
                Object.assign((e.target as HTMLButtonElement).style, nodeHeaderButtonActive);
              }
            }}
            onMouseLeave={(e) => {
              if (!isInspected && !hasErrors) {
                Object.assign((e.target as HTMLButtonElement).style, nodeHeaderButton);
              }
            }}
            title={hasErrors ? "Node has errors - click to inspect" : "Inspect node"}
          >
            ⚙
          </button>
        )}
        {(
          <button
            onClick={(e) => {
              e.stopPropagation();
              tryDelete();
            }}
            style={nodeHeaderButton}
            onMouseEnter={(e) => {
              (e.target as HTMLButtonElement).style.background = '#dc2626';
              (e.target as HTMLButtonElement).style.color = '#ffffff';
            }}
            onMouseLeave={(e) => {
              Object.assign((e.target as HTMLButtonElement).style, nodeHeaderButton);
            }}
            title="Delete node"
          >
            ✕
          </button>
        )}
      </div>
    </div>
  );
}
